/**
 * How the player acts, in two layers:
 *   INTERACTIONS - the action's `interaction` in the mission data (backend mechanics/core.py has the
 *                  same ids). Each says how to do it and how to hint at it:
 *                    howTo(action, h)  the second hint ("Click the Pallet, then 🫸")
 *                    nudge(action, h)  the first hint ("The Pallet is in the way of something")
 *                    control(action, h) which CONTROL the player uses for it
 *   CONTROLS     - what the player physically does in the scene (a drag in the data is a carry, a lead
 *                  or a push); each has its words for Mission Notes and How to Play.
 *   slotField    - the field that tells this interaction's controls apart on one item (backend: same)
 * `h` is the player's helpers: { nameOf(id), aboutOf(action), isCarryable(id), byId }.
 */
import { createRegistry } from './registry';

export const CONTROLS = createRegistry('control');
CONTROLS.register({ id: 'carry', how: 'collect things in the 🎒 and use them where they belong', line: '🎒 Things that belong elsewhere: click to collect, pick from the 🎒, click where it goes.' });
CONTROLS.register({ id: 'lead', how: 'lead people with 🚶', line: '🚶 People who need to be elsewhere: click them, tap 🚶, click where they go.' });
CONTROLS.register({ id: 'push', how: 'push things clear with 🫸', line: '🫸 Things in the way: click them, then 🫸 to push them clear.' });
CONTROLS.register({ id: 'write', how: 'write in records', line: '✍️ Records: open it, tap what the entry is about to put it on the new line, then sign.' });
CONTROLS.register({ id: 'hold', how: 'press and hold', line: '🔑 Some things take a press-and-hold: open the item and hold until it finishes.' });
CONTROLS.register({ id: 'switch', how: 'flip switches', line: '🔀 Switches: open the item and flip it.' });
CONTROLS.register({ id: 'gesture', how: 'gesture to people', line: '☝️ People: click them for their ring of gestures.' });
CONTROLS.register({ id: 'exit', how: 'the 🚪 walks you out', line: '🚪 The door walks you out - only leave when the job is done.' });
CONTROLS.register({ id: 'confirm', how: 'use the buttons on things', line: '👆 Open a thing to see what you can do with it - each button says what it does.' });
CONTROLS.register({ id: 'choose', how: 'pick the right option', line: '☑️ Some things ask you to pick: choose the option that matches what you were told.' });
CONTROLS.register({ id: 'adjust', how: 'measure amounts with − and +', line: '⚖️ Amounts: open the item, pour more (+) or less (−) until the level matches what\'s written.' });

const away = (a) => !a.drop_on || a.drop_on === 'away';

export const INTERACTIONS = createRegistry('interaction');
INTERACTIONS.register({
  id: 'drag',
  slotField: 'drop_on',
  // A move: carried in the 🎒 (things that go somewhere), pushed clear ('away'), or a person led.
  control: (a, h) => (h.isCarryable(a.item) ? 'carry' : away(a) ? 'push' : 'lead'),
  howTo: (a, h) => {
    if (away(a)) return `Click ${h.nameOf(a.item)}, then 🫸 to push it clear.`;
    if (h.isCarryable(a.item)) return `Click ${h.nameOf(a.item)} to put it in your 🎒, pick it from the 🎒, then click ${h.nameOf(a.drop_on)}.`;
    return `Click ${h.nameOf(a.item)}, tap 🚶, then click ${h.nameOf(a.drop_on)}.`;
  },
  nudge: (a, h) => (away(a) ? `${h.nameOf(a.item)} is in the way of something.` : `${h.nameOf(a.item)} isn't where it belongs.`)
});
INTERACTIONS.register({
  id: 'write',
  slotField: 'about',
  howTo: (a, h) => (h.aboutOf(a)
    ? `Open ${h.nameOf(a.item)}, tap ${h.nameOf(h.aboutOf(a))} to put it on the new line, then sign ✍️.`
    : `Open ${h.nameOf(a.item)}, put what you saw that's wrong on the new line, then sign ✍️.`),
  nudge: (a, h) => (h.aboutOf(a) ? `Something about ${h.nameOf(h.aboutOf(a))} should be on record.` : `Something you've seen should go in ${h.nameOf(a.item)}.`)
});
INTERACTIONS.register({
  id: 'hold',
  howTo: (a, h) => (h.byId[a.item]?.kind === 'person' ? `Click ${h.nameOf(a.item)}, then press and hold 🤝.` : `Open ${h.nameOf(a.item)} and press and hold 🔑 until it's done.`)
});
INTERACTIONS.register({ id: 'switch', howTo: (a, h) => `Open ${h.nameOf(a.item)} and flip its switch.` });
INTERACTIONS.register({
  id: 'gesture',
  howTo: (a, h) => `Click ${h.nameOf(a.item)} and use ☝️.`,
  nudge: (a, h) => `${h.nameOf(a.item)} needs a word from you.`
});
INTERACTIONS.register({ id: 'exit', howTo: () => 'Walk out through the 🚪.', nudge: () => 'The job here is done.' });
// An inventory item's buttons (inventory.py mechanics): confirm = one button, choose = pick one option.
const actionLabel = (a) => String(a.name || '').replace(/\s*\(.*\)$/, '');
INTERACTIONS.register({
  id: 'confirm',
  slotField: 'control',           // each of an item's buttons is its own control (as backend core.py _Confirm)
  howTo: (a, h) => `Open ${h.nameOf(a.item)} and press “${actionLabel(a)}”.`,
  nudge: (a, h) => `${h.nameOf(a.item)} needs you.`
});
INTERACTIONS.register({
  id: 'choose',
  slotField: 'value',
  howTo: (a, h) => `Open ${h.nameOf(a.item)} and pick the option that matches what you were told.`,
  nudge: (a, h) => `Something needs choosing at ${h.nameOf(a.item)}.`
});
INTERACTIONS.register({
  id: 'adjust',
  slotField: 'value',
  howTo: (a, h) => `Open ${h.nameOf(a.item)} and use − / + until it reads ${a.value}${h.byId[a.item]?.amount?.unit ? ` ${h.byId[a.item].amount.unit}` : ''}.`,
  nudge: (a, h) => `Check the amount in ${h.nameOf(a.item)} against what's written down.`
});

const fallbackNudge = (a, h) => `Take a closer look at ${h.nameOf(a.item)}.`;

/** The control the player uses for an action (its interaction's own id unless it says otherwise). */
export const controlOf = (a, h) => {
  const i = INTERACTIONS.get(a.interaction);
  return i && i.control ? i.control(a, h) : a.interaction;
};
export const howTo = (a, h) => INTERACTIONS.get(a.interaction)?.howTo?.(a, h);
export const nudge = (a, h) => (INTERACTIONS.get(a.interaction)?.nudge || fallbackNudge)(a, h);
/** "collect things in the 🎒 ..., push things clear with 🫸" - every control these actions use, once. */
export const howToAct = (actions, h) => [...new Set(actions.map((a) => CONTROLS.get(controlOf(a, h))?.how).filter(Boolean))].join(', ');
